import { motion } from 'framer-motion'
import {
  ArrowRight,
  Download,
  MessageCircle,
  Award,
  CheckCircle,
  Zap,
  Users,
  TrendingUp,
  Star
} from 'lucide-react'
import { Link } from 'react-router-dom'
import env from '@utils/env'
import StatsSection from '@components/StatsSection'
import TestimonialsSection from '@components/TestimonialsSection'
import CoursesSection from '@components/CoursesSection'
import WhyChooseUs from '@components/WhyChooseUs'
import BatchScheduleSection from '@components/BatchScheduleSection'
import MarketingSection from '@components/MarketingSection'

const heroStats = [
  { number: '500+', label: 'Students Trained', icon: <Users className="w-4 h-4" /> },
  { number: '95%', label: 'Placement Rate', icon: <TrendingUp className="w-4 h-4" /> },
  { number: '50+', label: 'Corporate Clients', icon: <Award className="w-4 h-4" /> },
  { number: '4.8★', label: 'Average Rating', icon: <Star className="w-4 h-4" /> },
]

const trustedBy = [
  { name: 'Google',    color: '#4285F4' },
  { name: 'Amazon',   color: '#FF9900' },
  { name: 'Microsoft',color: '#00A4EF' },
  { name: 'IBM',      color: '#1F70C1' },
  { name: 'Oracle',   color: '#F80000' },
  { name: 'Cisco',    color: '#1BA0D7' },
  { name: 'Infosys',  color: '#007CC3' },
  { name: 'TCS',      color: '#C00' },
  { name: 'Wipro',    color: '#341C6E' },
  { name: 'Accenture',color: '#A100FF' },
  { name: 'Deloitte', color: '#86BC25' },
  { name: 'Capgemini',color: '#0070AD' },
]

const roadmapSteps = [
  {
    step: '01',
    icon: <TerminalIcon />,
    title: 'Linux Fundamentals',
    desc: 'Master the command line and system administration',
    color: 'bg-primary-50 text-primary-600',
  },
  {
    step: '02',
    icon: <GitIcon />,
    title: 'Version Control',
    desc: 'Git workflows, branching, and team collaboration',
    color: 'bg-accent-50 text-accent-600',
  },
  {
    step: '03',
    icon: <ContainerIcon />,
    title: 'Containerization',
    desc: 'Docker, Kubernetes, and container orchestration',
    color: 'bg-primary-50 text-primary-600',
  },
  {
    step: '04',
    icon: <CloudIcon />,
    title: 'Cloud Platforms',
    desc: 'AWS, Azure, and Google Cloud infrastructure',
    color: 'bg-accent-50 text-accent-600',
  },
]

const container = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } }
const item = { hidden: { y: 24, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { type: 'spring', stiffness: 90 } } }

const Home = () => {
  return (
    <div className="min-h-screen">

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-primary-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-400/10 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -right-32 w-[30rem] h-[30rem] bg-accent-300/10 rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-50 dark:opacity-35 [background-image:radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.08)_1px,transparent_0),linear-gradient(130deg,rgba(59,130,246,0.09),transparent_60%)] dark:[background-image:radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.12)_1px,transparent_0),linear-gradient(130deg,rgba(59,130,246,0.14),transparent_60%)] [background-size:20px_20px,100%_100%]" />
        </div>

        <div className="container-custom relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center py-16 lg:py-24">

            {/* Left */}
            <motion.div variants={container} initial="hidden" animate="visible" className="space-y-7">

              <motion.div variants={item}>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 text-xs font-semibold rounded-full tracking-wide uppercase">
                  <Zap className="w-3 h-3" />
                  India's #1 DevOps Institute · Since 2020
                </span>
              </motion.div>

              <motion.h1
                variants={item}
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white leading-[1.1] tracking-tight"
              >
                Become a{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 text-primary-600">DevOps & Cloud</span>
                  <span className="absolute bottom-1 left-0 right-0 h-3 bg-accent-300/50 -skew-x-3 z-0 rounded" />
                </span>
                {' '}Engineer
              </motion.h1>

              <motion.p variants={item} className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed max-w-lg">
                Real industry training with hands-on labs, live projects, and expert mentorship.
                Land a high-paying DevOps role in months — not years.
              </motion.p>

              <motion.div variants={item} className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl shadow-lg shadow-primary-600/25 transition-all hover:shadow-xl hover:shadow-primary-600/30 hover:-translate-y-0.5"
                >
                  Enroll Now
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <button className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-primary-300 hover:text-primary-700 hover:bg-primary-50 transition-all">
                  <Download className="w-4 h-4" />
                  Download Curriculum
                </button>
                <a
                  href={`https://wa.me/${env.whatsappNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-xl shadow-lg shadow-green-500/25 transition-all hover:-translate-y-0.5"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp Us
                </a>
              </motion.div>

              <motion.div variants={item}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  {heroStats.map((stat, i) => (
                    <motion.div
                      key={i}
                      whileHover={{ y: -4, scale: 1.05 }}
                      className="relative group"
                    >
                      <div className="relative flex flex-col items-center gap-2 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg hover:shadow-xl transition-all duration-300 text-center overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-accent-500/5 dark:from-primary-500/10 dark:to-accent-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 text-white mb-1">
                          {stat.icon}
                        </div>
                        <span className="relative z-10 text-2xl font-extrabold bg-gradient-to-br from-primary-600 to-accent-500 bg-clip-text text-transparent">{stat.number}</span>
                        <span className="relative z-10 text-xs text-gray-600 dark:text-gray-400 font-semibold leading-tight">{stat.label}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </motion.div>

            {/* Right – Video card */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-large border border-gray-200/70 dark:border-gray-700/70 aspect-video">
                <img
                  src="/hero-instructor.jpg"
                  alt="Sudhan K S, Senior Cloud Architect and lead instructor at DeOrigin Labs"
                  width="1600"
                  height="894"
                  fetchPriority="high"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>

              <motion.div
                animate={{ y: [-8, 8, -8] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-5 -left-5 bg-white dark:bg-gray-800 rounded-2xl shadow-large p-4 border border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">Job Guarantee</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">6-Month Program</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [8, -8, 8] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="absolute -bottom-5 -right-5 bg-white dark:bg-gray-800 rounded-2xl shadow-large p-4 border border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center shrink-0">
                    <Award className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900 dark:text-white">Industry Certified</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Globally Recognized</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Trusted By ── */}
      <section className="py-10 bg-white dark:bg-gray-900 border-y border-gray-100 dark:border-gray-800 overflow-hidden">
        <p className="text-center text-xs font-semibold tracking-widest text-gray-400 dark:text-gray-500 uppercase mb-7">
          Our alumni work at
        </p>
        <style>{`
          @keyframes marquee-scroll {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .marquee-track {
            display: flex;
            width: max-content;
            animation: marquee-scroll 100s linear infinite;
          }
          .marquee-track:hover { animation-play-state: paused; }
        `}</style>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 z-10 bg-gradient-to-r from-white dark:from-gray-900 to-transparent pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 z-10 bg-gradient-to-l from-white dark:from-gray-900 to-transparent pointer-events-none" />
          <div className="marquee-track">
            {[...trustedBy, ...trustedBy].map((brand, i) => (
              <span
                key={i}
                style={{ '--brand-color': brand.color }}
                className="mx-8 text-xl font-extrabold text-gray-200 dark:text-gray-700 cursor-default select-none transition-all duration-300 hover:text-[var(--brand-color)] hover:scale-110 whitespace-nowrap"
              >
                {brand.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <StatsSection />

      {/* ── Courses ── */}
      <CoursesSection />

      {/* ── Why Choose Us ── */}
      <WhyChooseUs />

      {/* ── DevOps Roadmap Preview ── */}
      <section className="section-padding bg-gray-50 dark:bg-gray-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-400 text-xs font-semibold rounded-full uppercase tracking-wide mb-3">
              Structured Learning
            </span>
            <h2 className="text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white mb-3">
              Your DevOps Career Path
            </h2>
            <p className="text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
              Follow our structured roadmap from zero to a certified DevOps engineer
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {roadmapSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 shadow-soft hover:shadow-medium transition-shadow group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${step.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    {step.icon}
                  </div>
                  <span className="text-4xl font-black text-gray-100 leading-none">{step.step}</span>
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-1.5">{step.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/devops-roadmap"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-primary-600 text-primary-600 font-semibold rounded-xl hover:bg-primary-600 hover:text-white transition-all"
            >
              View Complete Roadmap
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Batch Schedule ── */}
      <BatchScheduleSection />

      {/* ── Testimonials ── */}
      <TestimonialsSection />

      {/* ── Marketing ── */}
      <MarketingSection />

      {/* ── Final CTA ── */}
      <section className="section-padding bg-primary-700 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-primary-500/40 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-primary-900/40 rounded-full blur-3xl" />
        </div>
        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center text-white max-w-3xl mx-auto"
          >
            <h2 className="text-3xl lg:text-4xl font-extrabold mb-4">
              Ready to Transform Your Career?
            </h2>
            <p className="text-lg text-primary-100 mb-8">
              Join 500+ successful DevOps engineers who started their journey with Deoriginlabs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white text-primary-700 font-bold rounded-xl hover:bg-gray-50 transition-all shadow-xl hover:-translate-y-0.5"
              >
                <Zap className="w-4 h-4" />
                Start Your DevOps Career
              </Link>
              <a
                href={`https://wa.me/${env.whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 border-2 border-white/40 text-white font-bold rounded-xl hover:bg-white/10 transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                Talk to a Mentor
              </a>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  )
}

function TerminalIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  )
}

function GitIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22" />
    </svg>
  )
}

function ContainerIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  )
}

function CloudIcon() {
  return (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 0 0 4 4h9a5 5 0 0 0 4.5-7.5 4 4 0 0 0-.5-7.5 5 5 0 0 0-9.5-1A4 4 0 0 0 3 15z" />
    </svg>
  )
}

export default Home
